import { ImagePreviewStatus } from 'components/ImagePreview';

export enum LicenseCameraScanStepsEnum {
  licenseFrontScan = 0,
  licenseFrontPreview = 1,
  licenseBackScan = 2,
  licenseBackPreview = 3,
  temporaryLicense = 4,
  endOfTheFlow = 5,
}

export interface LicenseCameraScanState {
  step: LicenseCameraScanStepsEnum;
  imageStatus?: ImagePreviewStatus;
  imageUploadProgress?: number;
  imageErrorMessage?: string;
  imageErrorLabel?: string;
  licenseFront?: string;
  licenseBack?: string;
  imagePreview?: string;
  showFlipMessage?: boolean;
  isPaused?: boolean;
  focusQuality?: number;
  focusQualityThreshold?: number;
  isBlankImage?: boolean;
  isWhiteImage?: boolean;
  useManualUpload?: boolean;
  file?: File;
}

export const initialState: LicenseCameraScanState = {
  step: LicenseCameraScanStepsEnum.licenseFrontScan,
};

export enum LicenseCameraScanActionTypes {
  NEXT_STEP,
  PREVIOUS_STEP,
  HIDE_FLIP_MESSAGE,
  SET_IMAGE_STATUS,
  SET_IMAGE_UPLOAD_PROGRESS,
  SET_USE_MANUAL_UPLOAD,
}

interface LicenseCameraScanActionPayload {
  type: LicenseCameraScanActionTypes;
  payload: any;
}

export function nextStepAction(
  mainImageData?: {
    mainImage: string;
    focusQuality: number;
    focusQualityThreshold: number;
    file?: File;
    isBlankImage?: boolean;
    isWhiteImage?: boolean;
  },
  previewImage?: string
) {
  return {
    type: LicenseCameraScanActionTypes.NEXT_STEP,
    payload: {
      mainImage: mainImageData?.mainImage,
      previewImage,
      focusQuality: mainImageData?.focusQuality,
      focusQualityThreshold: mainImageData?.focusQualityThreshold,
      file: mainImageData?.file,
      isBlankImage: mainImageData?.isBlankImage,
      isWhiteImage: mainImageData?.isWhiteImage,
    },
  };
}

export function previousStepAction() {
  return {
    type: LicenseCameraScanActionTypes.PREVIOUS_STEP,
    payload: null,
  };
}

export function hideFlipMessageAction() {
  return {
    type: LicenseCameraScanActionTypes.HIDE_FLIP_MESSAGE,
    payload: null,
  };
}

export function setImageStatusAction(
  imageStatus: ImagePreviewStatus,
  imageErrorMessage?: string,
  imageErrorLabel?: string
) {
  return {
    type: LicenseCameraScanActionTypes.SET_IMAGE_STATUS,
    payload: { imageStatus, imageErrorMessage, imageErrorLabel },
  };
}

export function setImageUloadProgressAction(imageUploadProgress: number) {
  return {
    type: LicenseCameraScanActionTypes.SET_IMAGE_UPLOAD_PROGRESS,
    payload: imageUploadProgress,
  };
}

export function setUseManualUploadAction(useManualUpload: boolean) {
  return {
    type: LicenseCameraScanActionTypes.SET_USE_MANUAL_UPLOAD,
    payload: useManualUpload,
  };
}

export function reducer(
  state: LicenseCameraScanState,
  action: LicenseCameraScanActionPayload
): LicenseCameraScanState {
  switch (action.type) {
    case LicenseCameraScanActionTypes.NEXT_STEP:
      switch (state.step) {
        case LicenseCameraScanStepsEnum.licenseFrontScan:
          return goToLicenseFrontPreviewStep(state, action);

        case LicenseCameraScanStepsEnum.licenseFrontPreview:
          return goToLicenseBackScanStep(state);

        case LicenseCameraScanStepsEnum.licenseBackScan:
          return goToLicenseBackPreviewStep(state, action);

        case LicenseCameraScanStepsEnum.licenseBackPreview:
          return goToTemporaryLicenseFlow(state);

        case LicenseCameraScanStepsEnum.temporaryLicense:
          return goToEndOfTheFlow(state);

        default:
          return state;
      }

    case LicenseCameraScanActionTypes.PREVIOUS_STEP:
      return {
        ...state,
        step: (state.step - 1) as LicenseCameraScanStepsEnum,
        isPaused: false,
        imageStatus: undefined,
        imageErrorMessage: undefined,
      };

    case LicenseCameraScanActionTypes.HIDE_FLIP_MESSAGE:
      return { ...state, showFlipMessage: false };

    case LicenseCameraScanActionTypes.SET_IMAGE_STATUS:
      return {
        ...state,
        imageStatus: action.payload.imageStatus,
        imageErrorMessage: action.payload.imageErrorMessage,
        imageErrorLabel: action.payload.imageErrorLabel,
      };

    case LicenseCameraScanActionTypes.SET_IMAGE_UPLOAD_PROGRESS:
      return {
        ...state,
        imageUploadProgress: action.payload,
      };

    case LicenseCameraScanActionTypes.SET_USE_MANUAL_UPLOAD:
      return {
        ...state,
        useManualUpload: action.payload,
      };

    default:
      return state;
  }
}

function goToLicenseFrontPreviewStep(
  state: LicenseCameraScanState,
  action: LicenseCameraScanActionPayload
): LicenseCameraScanState {
  return {
    ...state,
    step: LicenseCameraScanStepsEnum.licenseFrontPreview,
    licenseFront: action.payload.mainImage,
    imagePreview: action.payload.previewImage,
    focusQuality: action.payload.focusQuality,
    focusQualityThreshold: action.payload.focusQualityThreshold,
    file: action.payload.file,
    isPaused: true,
    imageStatus: ImagePreviewStatus.UPLOADING,
    imageErrorMessage: undefined,
  };
}

function goToLicenseBackScanStep(
  state: LicenseCameraScanState
): LicenseCameraScanState {
  return {
    ...state,
    step: LicenseCameraScanStepsEnum.licenseBackScan,
    showFlipMessage: true,
    isPaused: false,
    imageStatus: undefined,
    imageErrorMessage: undefined,
  };
}

function goToLicenseBackPreviewStep(
  state: LicenseCameraScanState,
  action: LicenseCameraScanActionPayload
): LicenseCameraScanState {
  return {
    ...state,
    step: LicenseCameraScanStepsEnum.licenseBackPreview,
    licenseBack: action.payload.mainImage,
    imagePreview: action.payload.previewImage,
    focusQuality: action.payload.focusQuality,
    focusQualityThreshold: action.payload.focusQualityThreshold,
    file: action.payload.file,
    isPaused: true,
    imageStatus: ImagePreviewStatus.UPLOADING,
    imageErrorMessage: undefined,
  };
}

function goToTemporaryLicenseFlow(
  state: LicenseCameraScanState
): LicenseCameraScanState {
  return {
    ...state,
    step: LicenseCameraScanStepsEnum.temporaryLicense,
    isPaused: true,
  };
}

function goToEndOfTheFlow(
  state: LicenseCameraScanState
): LicenseCameraScanState {
  return {
    ...state,
    step: LicenseCameraScanStepsEnum.endOfTheFlow,
    isPaused: true,
  };
}
